import {
  Typography,
  Box,
  Paper,
  IconButton,
  Button as MuiButton,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import React, { useState, useEffect } from 'react';
import FormSection from 'application/components/FormSection/FormSection';
import useOrgData from 'application/hooks/useOrgData';
import {
  SecondaryButton,
  SectionTitle,
  StyledButton,
  SwitchLabel,
} from './configurationForms/styledFormComponents';
import _ from 'lodash';
import { GamifyToast } from 'application/common/CustomToasts';
import ToggleSwitch from 'application/components/ToggleSwitch/ToggleSwitch';
import { API, graphqlOperation, Storage } from 'aws-amplify';
const { v4: uuidv4 } = require('uuid');

const createOrgHistory = /* GraphQL */ `
  mutation CreateOrgHistory($input: CreateOrgHistoryInput!) {
    createOrgHistory(input: $input) {
      id
      orgID
      object
      createdAt
    }
  }
`;

export default function DamageReportConfig() {
  const { orgData, updateOrgData } = useOrgData();
  const [damageReportsEnabled, setDamageReportsEnabled] = useState(false);
  const [damageReportsLogo, setDamageReportsLogo] = useState('');
  const [damageReportsHeroImages, setDamageReportsHeroImages] = useState<
    string[]
  >([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    if (orgData) {
      const damageReports = orgData?.configParms?.damageReports || {};
      setDamageReportsEnabled(damageReports.enabled || false);
      setDamageReportsLogo(damageReports.logo || '');
      setDamageReportsHeroImages(damageReports.heroImages || []);
    }
  }, [orgData]);

  useEffect(() => {
    const originalDamageReports = orgData?.configParms?.damageReports || {};
    const currentDamageReports = {
      enabled: damageReportsEnabled,
      logo: damageReportsLogo,
      heroImages: damageReportsHeroImages,
    };
    setHasChanged(!_.isEqual(currentDamageReports, originalDamageReports));
  }, [
    orgData,
    damageReportsEnabled,
    damageReportsLogo,
    damageReportsHeroImages,
  ]);

  const handleReset = () => {
    const damageReports = orgData?.configParms?.damageReports || {};
    setDamageReportsEnabled(damageReports.enabled || false);
    setDamageReportsLogo(damageReports.logo || '');
    setDamageReportsHeroImages(damageReports.heroImages || []);
  };

  const uploadLogo = async (file: File) => {
    try {
      setUploadingLogo(true);
      const fileName = `damage-reports/logo/${orgData?.id}/${Date.now()}_${
        file.name
      }`;
      await Storage.put(fileName, file, { contentType: file.type });
      setDamageReportsLogo(fileName);
    } catch (error) {
      console.error('Error uploading logo:', error);
      GamifyToast.error('Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const uploadHeroImage = async (file: File) => {
    try {
      setUploadingHeroImage(true);
      const fileName = `damage-reports/hero-images/${
        orgData?.id
      }/${Date.now()}_${file.name}`;
      await Storage.put(fileName, file, { contentType: file.type });
      setDamageReportsHeroImages((prev) => [...prev, fileName]);
    } catch (error) {
      console.error('Error uploading hero image:', error);
      GamifyToast.error('Error uploading hero image');
    } finally {
      setUploadingHeroImage(false);
    }
  };

  const removeHeroImage = (index: number) => {
    setDamageReportsHeroImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const newConfigParms = {
      ...orgData?.configParms,
      damageReports: {
        enabled: damageReportsEnabled,
        logo: damageReportsLogo,
        heroImages: damageReportsHeroImages,
      },
    };
    const newOrgInput = {
      ...orgData,
      configParms: JSON.stringify(newConfigParms),
    };
    const historyItem = {
      id: uuidv4(),
      orgID: orgData?.id,
      createdAt: new Date().toISOString(),
      object: JSON.stringify(newConfigParms),
    };
    try {
      await API.graphql(
        graphqlOperation(createOrgHistory, { input: historyItem })
      );
      await updateOrgData(newOrgInput);
      GamifyToast.success('Damage report settings updated successfully');
    } catch (error) {
      console.error('Error saving damage report settings:', error);
      GamifyToast.error('Error saving damage report settings');
    }
  };

  const renderLogoUpload = () => (
    <FormSection>
      <SwitchLabel sx={{ mb: 2 }}>Logo</SwitchLabel>
      <Box
        sx={(theme) => ({
          border: `2px dashed ${theme.custom?.stroke || '#E0E0E0'}`,
          borderRadius: '12px',
          padding: 3,
          textAlign: 'center',
          backgroundColor: theme.custom?.background2 || '#F5F5F5',
        })}
      >
        {damageReportsLogo ? (
          <Box>
            <Box
              component="img"
              src={`https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
                damageReportsLogo
              )}`}
              alt="Logo"
              sx={{
                width: 120,
                height: 120,
                objectFit: 'contain',
                marginBottom: 2,
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            />
            <Box sx={{ mt: 1 }}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadLogo(file);
                }}
                style={{ display: 'none' }}
                id="logo-upload-standalone"
              />
              <label htmlFor="logo-upload-standalone">
                <MuiButton
                  variant="outlined"
                  component="span"
                  disabled={uploadingLogo}
                  sx={{ mr: 1 }}
                >
                  {uploadingLogo ? 'Uploading...' : 'Replace Logo'}
                </MuiButton>
              </label>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box
              sx={(theme) => ({
                width: 120,
                height: 120,
                border: `2px dashed ${theme.custom?.stroke || '#E0E0E0'}`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 15px auto',
                backgroundColor: theme.custom?.background3 || '#EEEEEE',
              })}
            >
              <Typography
                sx={(theme) => ({
                  color: theme.custom?.textLight || '#999',
                  fontSize: '0.9rem',
                })}
              >
                No Logo
              </Typography>
            </Box>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadLogo(file);
              }}
              style={{ display: 'none' }}
              id="logo-upload-standalone"
            />
            <label htmlFor="logo-upload-standalone">
              <MuiButton
                variant="outlined"
                component="span"
                disabled={uploadingLogo}
              >
                {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              </MuiButton>
            </label>
          </Box>
        )}
      </Box>
    </FormSection>
  );

  const renderHeroImages = () => (
    <FormSection>
      <SwitchLabel sx={{ mb: 2 }}>Hero Images</SwitchLabel>
      <Box
        sx={(theme) => ({
          border: `2px dashed ${theme.custom?.stroke || '#E0E0E0'}`,
          borderRadius: '12px',
          padding: 3,
          backgroundColor: theme.custom?.background2 || '#F5F5F5',
          minHeight: '120px',
        })}
      >
        {damageReportsHeroImages.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              marginBottom: 2,
            }}
          >
            {damageReportsHeroImages.map((image, index) => (
              <Box key={index} sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={`https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
                    image
                  )}`}
                  alt={`Hero ${index + 1}`}
                  sx={{
                    width: 180,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeHeroImage(index)}
                  sx={(theme) => ({
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: theme.custom?.primary || '#FF6A00',
                    color: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: theme.custom?.primaryDark || '#E55A00',
                    },
                  })}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={(theme) => ({
              textAlign: 'center',
              padding: 3,
              color: theme.custom?.textLight,
            })}
          >
            <Typography
              sx={(theme) => ({
                fontSize: '0.9rem',
                marginBottom: 1,
                color: theme.custom?.textLight,
              })}
            >
              No hero images uploaded yet
            </Typography>
          </Box>
        )}
        <Box sx={{ textAlign: 'center' }}>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadHeroImage(file);
            }}
            style={{ display: 'none' }}
            id="hero-image-upload-standalone"
          />
          <label htmlFor="hero-image-upload-standalone">
            <MuiButton
              variant="outlined"
              component="span"
              disabled={uploadingHeroImage}
              startIcon={<Add />}
            >
              {uploadingHeroImage ? 'Uploading...' : 'Add Hero Image'}
            </MuiButton>
          </label>
        </Box>
      </Box>
    </FormSection>
  );

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.custom?.background1,
        borderRadius: '16px',
        marginBottom: 2,
      })}
    >
      <Typography
        sx={(theme) => ({
          fontWeight: 780,
          fontSize: 20,
          color: theme.custom?.text,
        })}
      >
        Damage Reports
      </Typography>
      <Typography
        sx={(theme) => ({
          fontWeight: 400,
          fontSize: 15,
          color: theme.custom?.textLight,
          marginBottom: 2,
        })}
      >
        Configure damage report settings for your organization.
      </Typography>

      <FormSection>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <SectionTitle>Damage Reports</SectionTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SwitchLabel>Enabled</SwitchLabel>
            <ToggleSwitch
              checked={damageReportsEnabled}
              onChange={(checked: boolean) => setDamageReportsEnabled(checked)}
            />
          </Box>
        </Box>

        {damageReportsEnabled && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {renderLogoUpload()}
            {renderHeroImages()}
          </Box>
        )}
      </FormSection>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <SecondaryButton onClick={handleReset} disabled={!hasChanged}>
          Discard Changes
        </SecondaryButton>
        <StyledButton onClick={handleSave} disabled={!hasChanged}>
          Save
        </StyledButton>
      </Box>
    </Paper>
  );
}
